import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { Button } from '@/components/Button'
import { Card } from '@/components/Card'
import { calendarioService, Calendario } from '@/services/calendario'
import { Calendar, Trash2, Edit2, Plus, FileText, Download } from 'lucide-react'

export function CalendariosListPage() {
  const [calendarios, setCalendarios] = useState<Calendario[]>([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState<number | null>(null)

  const loadCalendarios = async () => {
    try {
      setLoading(true)
      const response = await calendarioService.getAll()
      
      if (response.success && response.data) {
        setCalendarios(response.data)
      } else {
        toast.error(response.error || 'Erro ao carregar calendários')
      }
    } catch (error: any) {
      console.error('Erro ao carregar calendários:', error)
      toast.error('Erro ao carregar calendários')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Tem certeza que deseja excluir este calendário? Esta ação não pode ser desfeita.')) {
      return
    }

    try {
      setDeleting(id)
      const response = await calendarioService.delete(id)
      
      if (response.success) {
        toast.success('Calendário excluído com sucesso!')
        await loadCalendarios()
      } else {
        toast.error(response.error || 'Erro ao excluir calendário')
      }
    } catch (error: any) {
      console.error('Erro ao deletar:', error)
      toast.error('Erro ao excluir calendário')
    } finally {
      setDeleting(null)
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Não informado'
    
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return 'Data inválida'
      
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (error) {
      return 'Data inválida'
    }
  }

  const formatLastUpdate = (updatedAt: string | null | undefined) => {
    if (!updatedAt) return 'Nunca atualizado'
    
    try {
      const date = new Date(updatedAt)
      if (isNaN(date.getTime())) return 'Data inválida'
      
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (error) {
      return 'Data inválida'
    }
  }

  const getPeriodoLabel = (periodo: number) => {
    const periodos: Record<number, string> = {
      1: '1º Período',
      2: '2º Período',
      3: '3º Período',
      4: '4º Período'
    }
    return periodos[periodo] || `${periodo}º Período`
  }

  const getTipoCalendarioLabel = (tipo: number) => {
    const tipos: Record<number, string> = {
      1: 'Principal',
      2: 'Subcalendário'
    }
    return tipos[tipo] || `Tipo ${tipo}`
  }

  const getVigenteColor = (vigente: number) => {
    return vigente === 1 
      ? 'bg-green-100 text-green-700' 
      : 'bg-gray-100 text-gray-700'
  }

  const getVigenteLabel = (vigente: number) => {
    return vigente === 1 ? 'Vigente' : 'Não Vigente'
  }

  useEffect(() => {
    loadCalendarios()
  }, [])

  return (
    <div className="w-full">
      {/* Cabeçalho da página */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <Calendar className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Calendários Acadêmicos</h1>
            <p className="text-gray-600">Gerenciar calendários e documentos acadêmicos</p>
          </div>
        </div>
        
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={() => window.location.href = '/calendarios/novo'}
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Calendário
          </Button>
        </div>
      </div>

      {/* Conteúdo principal */}
      <Card>
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Carregando...</p>
          </div>
        ) : calendarios.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhum calendário encontrado
            </h3>
            <p className="text-gray-600 mb-4">
              Comece adicionando seu primeiro calendário acadêmico
            </p>
            <Button
              onClick={() => window.location.href = '/calendarios/novo'}
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Calendário
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {calendarios.map((calendario) => (
              <div
                key={calendario.id_calendario}
                className="border border-gray-200 rounded-lg p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <FileText className="w-6 h-6 text-blue-600" />
                      <h3 className="text-xl font-semibold text-gray-900">
                        {calendario.nome_calendario}
                      </h3>
                      <span className="px-3 py-1 text-sm font-medium bg-blue-100 text-blue-700 rounded-full">
                        {calendario.ano}
                      </span>
                      <span className="px-3 py-1 text-sm font-medium bg-green-100 text-green-700 rounded-full">
                        {getPeriodoLabel(calendario.periodo)}
                      </span>
                      <span className="px-3 py-1 text-sm font-medium bg-purple-100 text-purple-700 rounded-full">
                        {getTipoCalendarioLabel(calendario.id_tipo_calendario)}
                      </span>
                      <span className={`px-3 py-1 text-sm font-medium rounded-full ${getVigenteColor(calendario.vigente)}`}>
                        {getVigenteLabel(calendario.vigente)}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 mb-4">
                      {calendario.descricao_calendario}
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>
                        <p><strong>Data de Cadastro:</strong></p>
                        <p>{formatDate(calendario.data_cadastro)}</p>
                      </div>
                      
                      <div>
                        <p><strong>Última Atualização:</strong></p>
                        <p>{formatLastUpdate(calendario.updatedAt)}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // TODO: Implementar download do arquivo
                        toast.success('Download iniciado')
                      }}
                      title="Baixar arquivo"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => window.location.href = `/calendarios/editar/${calendario.id_calendario}`}
                      title="Editar calendário"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(calendario.id_calendario)}
                      loading={deleting === calendario.id_calendario}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      title="Excluir calendário"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}
